import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Snackbar,
  Grid,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { usePatientFilter } from '../hooks/usePatientFilter'
import { journeyMilestonesAPI } from '../services/api'

// Milestone type options
const MILESTONE_TYPES = [
  { value: 'treatment', label: 'Treatment' },
  { value: 'appointment', label: 'Appointment' },
  { value: 'diagnosis', label: 'Diagnosis' },
]

// Treatment type options (from seed data)
const TREATMENT_TYPES = [
  { code: 'CHEMO', description: 'Chemotherapy' },
  { code: 'RAD', description: 'Radiation Therapy' },
  { code: 'LUMP', description: 'Lumpectomy' },
  { code: 'MAS', description: 'Mastectomy' },
  { code: 'IMM', description: 'Immunotherapy' },
  { code: 'PHYS', description: 'Physical Therapy' },
  { code: 'HORM', description: 'Hormonal Therapy' },
  { code: 'SPEECH', description: 'Speech Therapy' },
  { code: 'OTHER', description: 'Other Treatment' },
]

// Diagnosis type options (from seed data)
const DIAGNOSIS_TYPES = [
  { code: 'BREAST', stage: '0', description: 'Breast Cancer, Stage 0' },
  { code: 'BREAST', stage: '1', description: 'Breast Cancer, Stage 1' },
  { code: 'BREAST', stage: '2', description: 'Breast Cancer, Stage 2' },
  { code: 'BREAST', stage: '3', description: 'Breast Cancer, Stage 3' },
  { code: 'BREAST', stage: '4', description: 'Breast Cancer, Stage 4' },
]

// Milestone type styles
const milestoneTypeStyles = {
  treatment: { color: '#0f766e', label: 'Treatment' },
  appointment: { color: '#0284c7', label: 'Appointment' },
  diagnosis: { color: '#dc2626', label: 'Diagnosis' },
}

function MilestoneTypeIndicator({ type }) {
  const style = milestoneTypeStyles[type] || { color: '#71717a', label: type }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function JourneyMilestones() {
  const {
    selectedPatient,
    hasSelectedPatient,
    filterBySelectedPatient
  } = usePatientFilter()

  const [milestones, setMilestones] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingMilestone, setEditingMilestone] = useState(null)
  const [formData, setFormData] = useState({
    type: '',
    date: dayjs(),
    description: '',
    treatment_code: '',
    diagnosis_code: '',
    diagnosis_stage: '',
  })

  // DataGrid columns
  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
      renderCell: (params) => <MilestoneTypeIndicator type={params.value} />,
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 100,
      valueGetter: (params) => dayjs(params.value).format('MMM DD, YYYY'),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'detail',
      headerName: 'Detail',
      width: 180,
      valueGetter: (params) => {
        const { type, treatment_description, diagnosis_code, diagnosis_stage, diagnosis_description } = params.row;

        if (type === 'treatment') {
          return treatment_description || '-';
        } else if (type === 'diagnosis') {
          if (diagnosis_code && diagnosis_stage) {
            return `${diagnosis_code} Stage ${diagnosis_stage}`;
          }
          return diagnosis_description || '-';
        } else {
          return '-';
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  // Load milestones when patient is selected
  useEffect(() => {
    if (hasSelectedPatient()) {
      fetchMilestones()
    } else {
      setMilestones([])
    }
  }, [selectedPatient])

  const fetchMilestones = async () => {
    if (!hasSelectedPatient()) return

    try {
      setLoading(true)
      setError(null)
      const response = await journeyMilestonesAPI.getMilestonesByUserId(selectedPatient.id)
      setMilestones(response.data.result || response.data)
    } catch (err) {
      console.error('Error fetching health data:', err)
      setError('Failed to load health data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingMilestone(null)
    setFormData({
      type: '',
      date: dayjs(),
      description: '',
      treatment_code: '',
      diagnosis_code: '',
      diagnosis_stage: '',
    })
    setOpenDialog(true)
  }

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone)
    setFormData({
      type: milestone.type,
      date: dayjs(milestone.date),
      description: milestone.description,
      treatment_code: milestone.treatment_code || '',
      diagnosis_code: milestone.diagnosis_code || '',
      diagnosis_stage: milestone.diagnosis_stage || '',
    })
    setOpenDialog(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this health data entry?')) {
      return
    }

    try {
      setLoading(true)
      await journeyMilestonesAPI.deleteMilestone(id)
      setSuccess('Health data entry deleted successfully')
      fetchMilestones()
    } catch (err) {
      console.error('Error deleting health data entry:', err)
      setError('Failed to delete health data entry. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    // Validation
    if (!formData.type) {
      setError('Please select an entry type')
      return
    }
    if (!formData.description.trim()) {
      setError('Please enter a description')
      return
    }
    if (formData.type === 'treatment' && !formData.treatment_code) {
      setError('Please select a treatment type for treatment entries')
      return
    }
    if (formData.type === 'diagnosis' && (!formData.diagnosis_code || !formData.diagnosis_stage)) {
      setError('Please select both diagnosis code and stage for diagnosis entries')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const milestoneData = {
        user_id: selectedPatient.id,
        type: formData.type,
        date: formData.date.toISOString(),
        description: formData.description.trim(),
        treatment_code: formData.treatment_code || null,
        diagnosis_code: formData.diagnosis_code || null,
        diagnosis_stage: formData.diagnosis_stage || null,
      }

      if (editingMilestone) {
        await journeyMilestonesAPI.updateMilestone(editingMilestone.id, milestoneData)
        setSuccess('Health data entry updated successfully')
      } else {
        await journeyMilestonesAPI.createMilestone(milestoneData)
        setSuccess('Health data entry created successfully')
      }

      setOpenDialog(false)
      fetchMilestones()
    } catch (err) {
      console.error('Error saving health data entry:', err)
      setError(err.response?.data?.error || 'Failed to save health data entry. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingMilestone(null)
    setError(null)
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Clear related fields when type changes
      ...(field === 'type' && {
        treatment_code: '',
        diagnosis_code: '',
        diagnosis_stage: '',
      })
    }))
  }

  // Get filtered diagnosis options based on selected code
  const getDiagnosisStageOptions = () => {
    if (!formData.diagnosis_code) return []
    return DIAGNOSIS_TYPES
      .filter(d => d.code === formData.diagnosis_code)
      .map(d => ({ value: d.stage, label: `Stage ${d.stage}` }))
  }

  if (!hasSelectedPatient()) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Health Data
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select a patient from the dropdown above to view and manage their health data.
        </Alert>
      </Box>
    )
  }

  const formatDate = (value) => {
    if (!value) return '—'
    try {
      return dayjs(value).format('MMM D, YYYY')
    } catch (_) {
      return String(value)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5">Health Data</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={loading}
          >
            Add Entry
          </Button>
        </Box>

        {/* Patient info panel */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            {selectedPatient.name || 'Unnamed'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                {selectedPatient.id}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Age
              </Typography>
              <Typography variant="body2">{selectedPatient.age ?? '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Sex
              </Typography>
              <Typography variant="body2">{selectedPatient.sex || '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Type
              </Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {selectedPatient.user_type || '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Created
              </Typography>
              <Typography variant="body2">{formatDate(selectedPatient.created_at)}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          <DataGrid
                rows={milestones}
                columns={columns}
                pageSize={15}
                rowsPerPageOptions={[15, 25, 50]}
                autoHeight
                disableRowSelectionOnClick
                loading={loading}
                getRowId={(row) => row.id}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                      py={4}
                    >
                      <TimelineIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No health data entries found
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click "Add Entry" to create one.
                      </Typography>
                    </Box>
                  ),
                }}
              />
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingMilestone ? 'Edit Health Data Entry' : 'Add Health Data Entry'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Entry Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                    label="Entry Type"
                  >
                    {MILESTONE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date"
                  value={formData.date}
                  onChange={(newValue) => handleFormChange('date', newValue)}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  required
                />
              </Grid>

              {formData.type === 'treatment' && (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Treatment Type</InputLabel>
                    <Select
                      value={formData.treatment_code}
                      onChange={(e) => handleFormChange('treatment_code', e.target.value)}
                      label="Treatment Type"
                    >
                      {TREATMENT_TYPES.map((treatment) => (
                        <MenuItem key={treatment.code} value={treatment.code}>
                          {treatment.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {formData.type === 'diagnosis' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Diagnosis Code</InputLabel>
                      <Select
                        value={formData.diagnosis_code}
                        onChange={(e) => handleFormChange('diagnosis_code', e.target.value)}
                        label="Diagnosis Code"
                      >
                        {DIAGNOSIS_TYPES
                          .filter((d, index, arr) => arr.findIndex(item => item.code === d.code) === index)
                          .map((diagnosis) => (
                            <MenuItem key={diagnosis.code} value={diagnosis.code}>
                              {diagnosis.code}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Stage</InputLabel>
                      <Select
                        value={formData.diagnosis_stage}
                        onChange={(e) => handleFormChange('diagnosis_stage', e.target.value)}
                        label="Stage"
                        disabled={!formData.diagnosis_code}
                      >
                        {getDiagnosisStageOptions().map((stage) => (
                          <MenuItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {editingMilestone ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error/Success Messages */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}

export default JourneyMilestones 